import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const token = () => localStorage.getItem('session_token');
const LS = { voice: 'tts:voice', readAloud: 'tts:readIncoming' };

export const TTS_VOICES = [
  { id: 'nova', label: 'Nova', hint: 'energetic, upbeat' },
  { id: 'alloy', label: 'Alloy', hint: 'neutral, balanced' },
  { id: 'ash', label: 'Ash', hint: 'clear, articulate' },
  { id: 'coral', label: 'Coral', hint: 'warm, friendly' },
  { id: 'echo', label: 'Echo', hint: 'smooth, calm' },
  { id: 'fable', label: 'Fable', hint: 'expressive' },
  { id: 'onyx', label: 'Onyx', hint: 'deep, authoritative' },
  { id: 'sage', label: 'Sage', hint: 'wise, measured' },
  { id: 'shimmer', label: 'Shimmer', hint: 'bright, cheerful' },
];
export const DEFAULT_VOICE = 'nova';

export const getTtsVoice = () => localStorage.getItem(LS.voice) || DEFAULT_VOICE;
export const saveTtsVoice = (voice) => localStorage.setItem(LS.voice, voice);
export const getReadAloud = () => localStorage.getItem(LS.readAloud) === 'on';
export const saveReadAloud = (on) => localStorage.setItem(LS.readAloud, on ? 'on' : 'off');

export const synthesize = (text, voice = getTtsVoice()) => axios
  .post(`${API}/tts`, { text, voice }, { headers: { Authorization: `Bearer ${token()}` } })
  .then((r) => `${BACKEND_URL}${r.data.url}?auth=${token()}`);

// One app-wide speech player: a request interrupts what is playing unless it asks to queue.
const listeners = new Set();
let state = { id: null, phase: 'idle' }; // phase: idle | loading | playing
let audio = null;
let queue = [];
let busy = false;

const emit = (next) => { state = next; listeners.forEach((fn) => fn(state)); };

export const subscribeSpeech = (fn) => { listeners.add(fn); fn(state); return () => listeners.delete(fn); };

export function stopSpeech() {
  queue = [];
  if (audio) { audio.onended = null; audio.onerror = null; audio.pause(); audio.src = ''; audio = null; }
  busy = false;
  emit({ id: null, phase: 'idle' });
}

async function drain() {
  if (busy || !queue.length) return;
  busy = true;
  const item = queue.shift();
  emit({ id: item.id, phase: 'loading' });
  const done = () => { audio = null; busy = false; emit({ id: null, phase: 'idle' }); drain(); };
  try {
    const url = await synthesize(item.text, item.voice);
    if (!busy) return; // stopped while loading
    audio = new Audio(url);
    audio.onended = done;
    audio.onerror = () => { item.reject?.(new Error('Playback failed')); done(); };
    emit({ id: item.id, phase: 'playing' });
    await audio.play();
    item.resolve?.();
  } catch (err) {
    item.reject?.(err);
    done();
  }
}

// Resolves once playback has started; rejects on synthesis/playback errors.
export function speak(text, { id = null, voice = getTtsVoice(), enqueue = false } = {}) {
  if (!text || !text.trim()) return Promise.resolve();
  if (!enqueue) stopSpeech();
  return new Promise((resolve, reject) => {
    queue.push({ text, id, voice, resolve, reject });
    drain();
  });
}

export const speechErrorMessage = (err) => err?.response?.data?.detail
  || (err?.name === 'NotAllowedError' ? 'Tap the page once to allow audio, then try again' : 'Could not play speech');
